import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { IoIosArrowForward } from "react-icons/io";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { place_order } from "../store/reducers/orderReducer";
import { migrateGuestCart } from "../store/reducers/guestcartReducer";
import toast from "react-hot-toast";
import useGuestId from "../hooks/useGuestId";
import { customer_register, messageClear } from "../store/reducers/authReducer";
import CheckoutStepper from "../components/CheckoutStepper";

const Shipping = () => {
  const originUrl = window.location.origin;
  const bgStyle = { backgroundImage: `url(${originUrl}/images/banner/shop.png)` };

  // Obtener datos del carrito desde la ubicación
  const {
    state: { products, price, items },
  } = useLocation();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { currentCompany } = useSelector(state => state.init);

  const guestId = useGuestId();

  const [shipping_fee, setShippingFee] = useState(0);
  const [shippingMethod, setShippingMethod] = useState("sincosto");

  // Envío gratis si supera el monto configurado en company
  const sendFreeAmount = currentCompany?.sendFree_amount || 0;
  const mensajeEnvioGratis = currentCompany?.mensaje_envio_gratis || '🎉 ¡FELICIDADES! Tu envío sale GRATIS';
  const isFreeShipping = sendFreeAmount > 0 && price >= sendFreeAmount;

  // Auto-seleccionar envío gratis si aplica
  useEffect(() => {
    if (isFreeShipping) {
      setShippingFee(0);
      setShippingMethod("gratis");
    }
  }, [isFreeShipping]);
  // Estados de Redux
  const { errorMessage, successMessage, userInfo } = useSelector(state => state.auth);
  const { orderSuccess, orderId } = useSelector(state => state.order);

  // Estados locales - con inicialización desde sessionStorage
  const [loading, setLoading] = useState({
    registering: false,
    migrating: false,
    placingOrder: false,
    consulting: false,
    loadingUbigeo: false,
  });

  // Recuperar datos guardados de sessionStorage al inicializar
  const getInitialShippingInfo = () => {
    const saved = sessionStorage.getItem("shippingInfo");
    return saved
      ? JSON.parse(saved)
      : {
          name: "",
          phone: "",
          address: "",
          province: "",
          city: "",
          area: "-",
          tipoComprobante: "BOLETA",
          numeroComprobante: "",
        };
  };

  const getInitialRegisterInfo = () => {
    const saved = sessionStorage.getItem("registerInfo");
    return saved
      ? JSON.parse(saved)
      : {
          email: "",
          password: "-",
          accept_policies: false,
          personaldata: false,
          tipoDocumento: "DNI",
          numeroDocumento: "",
          lastname: "",
        };
  };

  const [shippingInfo, setShippingInfo] = useState(getInitialShippingInfo);
  const [registerInfo, setRegisterInfo] = useState(getInitialRegisterInfo);

  const [errors, setErrors] = useState({});
  const [razonSocial, setRazonSocial] = useState("");

  // Estados para el autocompletado de ubigeo
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [filteredDistricts, setFilteredDistricts] = useState([]);
  //const [ubigeoError, setUbigeoError] = useState(false);

  // Guardar datos en sessionStorage cuando cambien
  useEffect(() => {
    sessionStorage.setItem("shippingInfo", JSON.stringify(shippingInfo));
  }, [shippingInfo]);

  useEffect(() => {
    sessionStorage.setItem("registerInfo", JSON.stringify(registerInfo));
  }, [registerInfo]);

  // Limpiar sessionStorage al salir del componente
  useEffect(() => {
    return () => {
      // Solo limpiamos si no estamos yendo a la página de pago
      if (!window.location.pathname.includes("/pago")) {
        sessionStorage.removeItem("shippingInfo");
        sessionStorage.removeItem("registerInfo");
      }
    };
  }, []);

  // Función para obtener datos de ubigeo simplificados con useCallback
  const fetchUbigeoData = useCallback(async () => {
    try {
      console.log("Cargando datos de ubigeo...");

      // Opción 1: API principal
      let response = await fetch("https://raw.githubusercontent.com/joseluisq/ubigeos-peru/master/json/ubigeos.json");

      // Si falla la primera API, intentar con una alternativa
      if (!response.ok) {
        console.log("Intentando con API alternativa...");
        response = await fetch("https://api-territorial.apps.maddev.com/v1/ubigeo");
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Procesar datos para obtener provincias y distritos únicos
      const provincesMap = new Map();
      const districtsList = [];

      // Manejar diferentes formatos de respuesta
      const ubigeoData = Array.isArray(data) ? data : data.data || data.ubigeos || [];

      if (ubigeoData.length === 0) {
        throw new Error("No se encontraron datos de ubigeo");
      }

      ubigeoData.forEach(item => {
        // Extraer nombres según diferentes formatos de API
        const provinceName = item.province || item.provincia || item.nombre_provincia || "";
        const districtName = item.district || item.distrito || item.nombre_distrito || "";

        if (provinceName && !provincesMap.has(provinceName)) {
          provincesMap.set(provinceName, {
            name: provinceName,
            department: item.department || item.departamento || item.nombre_departamento || "",
          });
        }

        if (districtName && provinceName) {
          districtsList.push({
            name: districtName,
            province: provinceName,
          });
        }
      });

      const provincesArray = Array.from(provincesMap.values());

      // Ordenar alfabéticamente
      provincesArray.sort((a, b) => a.name.localeCompare(b.name));
      districtsList.sort((a, b) => a.name.localeCompare(b.name));

      setProvinces(provincesArray);
      setDistricts(districtsList);
      //setUbigeoError(false);

      // Guardar en localStorage para futuras visitas
      localStorage.setItem(
        "peru_ubigeo_simple",
        JSON.stringify({
          provinces: provincesArray,
          districts: districtsList,
          timestamp: new Date().getTime(),
        })
      );

      console.log("Datos de ubigeo cargados exitosamente");
    } catch (error) {
      console.error("Error fetching ubigeo data:", error);
      //setUbigeoError(true);

      // Intentar cargar datos de respaldo locales si las APIs fallan
      try {
        const backupData = {
          provinces: [
            { name: "Lima", department: "Lima" },
            { name: "Arequipa", department: "Arequipa" },
            { name: "Cusco", department: "Cusco" },
            { name: "Piura", department: "Piura" },
            { name: "La Libertad", department: "La Libertad" },
            { name: "Lambayeque", department: "Lambayeque" },
            { name: "Junín", department: "Junín" },
            { name: "Puno", department: "Puno" },
            { name: "Ancash", department: "Ancash" },
            { name: "Ica", department: "Ica" },
          ],
          districts: [
            { name: "Lima", province: "Lima" },
            { name: "Miraflores", province: "Lima" },
            { name: "Surco", province: "Lima" },
            { name: "Arequipa", province: "Arequipa" },
            { name: "Yanahuara", province: "Arequipa" },
            { name: "Cusco", province: "Cusco" },
            { name: "San Blas", province: "Cusco" },
            { name: "Piura", province: "Piura" },
            { name: "Trujillo", province: "La Libertad" },
            { name: "Chiclayo", province: "Lambayeque" },
          ],
        };

        setProvinces(backupData.provinces);
        setDistricts(backupData.districts);
        console.log("Usando datos de respaldo locales");
      } catch (backupError) {
        console.error("Error cargando datos de respaldo:", backupError);
        toast.error("Error al cargar datos de ubicación. Por favor, ingrese manualmente.");
      }
    }
  }, []);

  // Cargar datos de ubigeo al montar el componente
  useEffect(() => {
    const loadUbigeoData = async () => {
      setLoading(prev => ({ ...prev, loadingUbigeo: true }));
      //setUbigeoError(false);

      try {
        // Verificar si los datos en caché son recientes (menos de 7 días)
        const cachedUbigeo = localStorage.getItem("peru_ubigeo_simple");
        const now = new Date().getTime();
        const oneWeek = 7 * 24 * 60 * 60 * 1000; // 7 días en milisegundos

        if (cachedUbigeo) {
          const ubigeoData = JSON.parse(cachedUbigeo);

          // Si los datos tienen menos de una semana, usarlos
          if (ubigeoData.timestamp && now - ubigeoData.timestamp < oneWeek) {
            setProvinces(ubigeoData.provinces || []);
            setDistricts(ubigeoData.districts || []);
            console.log("Datos de ubigeo cargados desde caché");
            return;
          }
        }

        // Si no hay datos en caché o están vencidos, cargar desde la API
        await fetchUbigeoData();
      } catch (error) {
        console.error("Error al cargar datos de ubigeo:", error);
      //  setUbigeoError(true);
        toast.error("Error al cargar datos de ubicación. Por favor, ingrese manualmente.");
      } finally {
        setLoading(prev => ({ ...prev, loadingUbigeo: false }));
      }
    };

    loadUbigeoData();
  }, [fetchUbigeoData]);

  // Filtrar distritos cuando cambia la provincia
  useEffect(() => {
    if (shippingInfo.province && districts.length > 0) {
      const filtered = districts.filter(district => district.province === shippingInfo.province);
      setFilteredDistricts(filtered);

      // Resetear distrito si la provincia cambia
      if (shippingInfo.city && !filtered.some(d => d.name === shippingInfo.city)) {
        setShippingInfo(prev => ({
          ...prev,
          city: "",
        }));
      }
    }
  }, [shippingInfo.province,shippingInfo.city, districts]);

  // Consultar documento en ApisPeru
  const consultarDocumento = async (tipo, numero) => {
    if (!numero || (tipo === "DNI" && numero.length < 8) || (tipo === "RUC" && numero.length < 11)) return;

    setLoading(prev => ({ ...prev, consulting: true }));
    try {
      const token = process.env.REACT_APP_APISPERU_TOKEN;
      let url = "";

      if (tipo === "DNI") {
        url = `https://dniruc.apisperu.com/api/v1/dni/${numero}?token=${token}`;
      } else if (tipo === "RUC") {
        url = `https://dniruc.apisperu.com/api/v1/ruc/${numero}?token=${token}`;
      } else {
        return;
      }

      const response = await fetch(url);
      const data = await response.json();

      if (data.success || data.estado === "ACTIVO") {
        setShippingInfo(prev => ({
          ...prev,
          name: data.nombres || data.razonSocial || prev.name,
          address: data.direccion || prev.address,
          province: data.provincia || prev.province,
          city: data.distrito || prev.city,
          tipoComprobante: data.tipoComprobante || prev.tipoComprobante,
          numeroComprobante: data.numeroComprobante || prev.numeroComprobante,
        }));

        if (data.estado === "ACTIVO") {
          data.apellidoPaterno = "-";
          data.apellidoMaterno = "";
        }

        setRegisterInfo(prev => ({
          ...prev,
          lastname: data.apellidoPaterno ? `${data.apellidoPaterno} ${data.apellidoMaterno || ""}`.trim() : prev.lastname,
          ...(!userInfo && !prev.email && { email: data.correo || prev.email }),
        }));
      } else {
        toast.error(data.message || "No se encontraron datos para este documento");
      }
    } catch (error) {
      console.error("Error al consultar documento:", error);
      toast.error("Error al consultar los datos del documento");
    } finally {
      setLoading(prev => ({ ...prev, consulting: false }));
    }
  };

  const consultarRazonSocial = async ruc => {
    try {
      setLoading(prev => ({ ...prev, consulting: true }));

      const token = process.env.REACT_APP_APISPERU_TOKEN;
      const url = `https://dniruc.apisperu.com/api/v1/ruc/${ruc}?token=${token}`;

      const response = await fetch(url);
      const data = await response.json();
      if (data.razonSocial) {
        setRazonSocial(data.razonSocial);
      } else {
        setRazonSocial("No encontrado");
      }
    } catch (error) {
      setRazonSocial("Error al consultar");
    } finally {
      setLoading(prev => ({ ...prev, consulting: false }));
    }
  };

  // Normaliza número peruano a formato internacional +51XXXXXXXXX
  const normalizePhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.startsWith('51') && digits.length === 11) return `+${digits}`;
    if (digits.length === 9) return `+51${digits}`;
    return `+${digits}`;
  };

  // Manejar cambios en los inputs
  const handleShippingInput = e => {
    const { name, value } = e.target;

    // setShippingInfo({ ...shippingInfo, [name]: value });

    // Si cambia a BOLETA, limpiar el número de comprobante
    if (name === "tipoComprobante" && value === "BOLETA") {
      setShippingInfo({
        ...shippingInfo,
        [name]: value,
        numeroComprobante: "", // Limpiar el campo
      });
    } else {
      setShippingInfo({ ...shippingInfo, [name]: value });
    }
  };

  const handleRegisterInput = async e => {
    const { name, value } = e.target;
    setRegisterInfo({ ...registerInfo, [name]: value });

    if (name === "numeroDocumento") {
      if ((registerInfo.tipoDocumento === "DNI" && value.length === 8) || (registerInfo.tipoDocumento === "RUC" && value.length === 11)) {
        await consultarDocumento(registerInfo.tipoDocumento, value);
      }
    }

    if (name === "tipoDocumento" && ((value === "DNI" && registerInfo.numeroDocumento.length === 8) || (value === "RUC" && registerInfo.numeroDocumento.length === 11))) {
      await consultarDocumento(value, registerInfo.numeroDocumento);
    }
  };

  const handleCheckboxChange = e => {
    const { name, checked } = e.target;
    setRegisterInfo(prev => ({
      ...prev,
      [name]: checked,
    }));
  };

  // Efecto para rellenar email si el usuario está logueado
  useEffect(() => {
    if (userInfo) {
      console.log("verificar si userInfo ", userInfo);

      setRegisterInfo(prev => ({
        ...prev,
        email: userInfo.email,
        tipoDocumento: userInfo.tipoDocumento || "DNI",
        numeroDocumento: userInfo.numeroDocumento || "",
        lastname: userInfo.lastname || "",
      }));
      setShippingInfo({
        name: userInfo.name || "",
        address: userInfo.address || "",
        phone: userInfo.phone || "",
        province: userInfo.province || "",
        city: userInfo.city || "",
        area: "-",
        tipoComprobante: userInfo.tipoComprobante || "BOLETA", // Valor por defecto
        numeroComprobante: userInfo.numeroComprobante || "",
      });
    }
  }, [userInfo]);

  // Efecto para redirigir después de éxito en el pedido
  useEffect(() => {
    if (orderSuccess && orderId) {
      navigate("/pago", {
        state: {
          price: price + shipping_fee,
          items: items,
          orderId: orderId,
        },
      });
    }
  }, [orderSuccess, orderId, items, price, shipping_fee, navigate]);

  // Efecto para consultar RUC cuando cambia el tipo de comprobante
  useEffect(() => {
    if (shippingInfo.tipoComprobante === "FACTURA" && shippingInfo.numeroComprobante?.length === 11) {
      consultarRazonSocial(shippingInfo.numeroComprobante);
    }
  }, [shippingInfo.numeroComprobante, shippingInfo.tipoComprobante]);

  // Validar formulario
  const validateForm = () => {
    const requiredFields = {
      ...shippingInfo,
      ...registerInfo,
    };

    const newErrors = {};
    Object.entries(requiredFields).forEach(([key, value]) => {
      // No requerir numeroComprobante si es BOLETA
      if (key === "numeroComprobante" && shippingInfo.tipoComprobante === "BOLETA") {
        return; // Saltar validación para este campo
      }

      if (!value && key !== "personaldata" && key !== "area") {
        newErrors[key] = true;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Registrar nuevo cliente y devolver los datos del usuario
  const registerCustomer = async () => {
    if (!validateForm()) {
      toast.error("Por favor complete todos los campos requeridos");
      return null;
    }

    try {
      setLoading(prev => ({ ...prev, registering: true }));
      const registrationData = {
        ...shippingInfo,
        ...registerInfo,
        password: registerInfo.numeroDocumento,
        phone: normalizePhone(shippingInfo.phone),
        address: shippingInfo.address,
        province: shippingInfo.province,
        city: shippingInfo.city,
      };

      console.log("data registrationData", registrationData);

      // Disparamos la acción y esperamos la respuesta
      const response = await dispatch(customer_register(registrationData)).unwrap();
      return response.user;
    } catch (error) {
      console.error("Error al registrar:", error);
      toast.error(error?.message || "Error al registrar el cliente");
      return null;
    } finally {
      setLoading(prev => ({ ...prev, registering: false }));
    }
  };

  // Procesar todo el flujo del pedido
  const processOrder = async () => {
    try {
      let currentUser = userInfo;

      // 1. Registrar al cliente si no está logueado
      if (!currentUser) {
        currentUser = await registerCustomer();
        if (!currentUser) return; // Si el registro falla, salimos
      }

      // 2. Migrar carrito si es necesario
      if (guestId && currentUser?.id) {
        try {
          setLoading(prev => ({ ...prev, migrating: true }));
          await dispatch(migrateGuestCart({ userId: currentUser.id, guestId })).unwrap();
        } catch (error) {
          console.error("Error al migrar carrito:", error);
          toast.error("Error al migrar el carrito");
          throw error;
        } finally {
          setLoading(prev => ({ ...prev, migrating: false }));
        }
      }

      // 3. Crear el pedido
      try {
        setLoading(prev => ({ ...prev, placingOrder: true }));
        const orderResult = await dispatch(
          place_order({
            price,
            products,
            shipping_fee,
            items,
            shippingInfo: { ...shippingInfo, email: registerInfo.email, phone: normalizePhone(shippingInfo.phone) },
            userId: currentUser.id, // Usamos el ID del usuario actual (recién registrado o existente)
          })
        ).unwrap();

        // 4. Redirigir a página de pago
        if (orderResult?.orderId) {
          navigate("/pago", {
            state: {
              price: price + shipping_fee,
              items: items,
              orderId: orderResult.orderId,
            },
          });
        }
      } catch (error) {
        console.error("Error al crear pedido:", error);
        toast.error(error?.message || "Error al crear el pedido");
        throw error;
      } finally {
        setLoading(prev => ({ ...prev, placingOrder: false }));
      }
    } catch (error) {
      console.error("Error en el proceso:", error);
    }
  };

  // Efecto principal para manejar mensajes
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
    }

    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
  }, [dispatch, successMessage, errorMessage]);

  // Manejar envío del formulario
  const handleSubmit = async e => {
    e.preventDefault();

    // Guardar datos antes de navegar
    sessionStorage.setItem("shippingInfo", JSON.stringify(shippingInfo));
    sessionStorage.setItem("registerInfo", JSON.stringify(registerInfo));
    await processOrder();
  };

  return (
    <div>
      <Header />
      {currentCompany.verimageenvio && (
        <section className="h-[220px] mt-6 bg-cover bg-no-repeat relative bg-left" style={bgStyle}>
          <div className="absolute left-0 top-0 w-full h-full bg-[#2422228a]">
            <div className="w-[85%] md:w-[80%] sm:w-[90%] lg:w-[90%] h-full mx-auto">
              <div className="flex flex-col justify-center gap-1 items-center h-full w-full text-white">
                <h2 className="text-3xl font-bold">Dirección de envío </h2>
                <div className="flex justify-center items-center gap-2 text-2xl w-full">
                  <Link to="/">Inicio</Link>
                  <span className="pt-1">
                    <IoIosArrowForward />
                  </span>
                  <span>Dirección de envío </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      <section className="bg-[#eeeeee]">
        <div className="w-[85%] lg:w-[90%] md:w-[90%] sm:w-[90%] mx-auto py-16">
          {currentCompany?.verCheckoutStepper && <CheckoutStepper currentStep={2} />}
          <div className="w-full flex flex-wrap">
            <div className="w-[67%] md-lg:w-full">
              {/*
              {!userInfo && (
                <div className="flex flex-col gap-3">
                  <div className="bg-white p-6 shadow-sm rounded-md">
                    <h2 className="text-slate-600 font-bold pb-3">¿Ya tienes una cuenta? </h2>
                    <p>¡Bienvenido!, si ya estás registrado, ¡Inicia sesión para ahorrar tiempo!</p>
                    <Link to="/login">
                      <div className="mt-2 px-8 w-full py-1 text-center bg-white border-2 border-pink-custom hover:bg-pink-50 text-gray-800 rounded-md transition-colors text-sm">INICIAR SESIÓN</div>
                    </Link>
                  </div>
                </div>
              )}
                */}

              <div className="flex flex-col gap-3">
                <div className="bg-white p-6 shadow-sm rounded-md">
                  <form onSubmit={handleSubmit}>
                    <h2 className="text-slate-600 font-bold pb-3">Datos de contacto </h2>
                    <div className="flex md:flex-col md:gap-2 w-full gap-5 text-slate-600">
                      <div className="flex flex-col gap-1 mb-2 w-full">
                        <label htmlFor="email">
                          Correo Electrónico <span className="text-red-500">*</span>
                        </label>
                        <input
                          onChange={handleRegisterInput}
                          value={registerInfo.email}
                          type="email"
                          className={`w-full px-3 py-2 outline-none rounded-md 
                            ${errors.email ? "border-2 border-red-500" : "bg-white border border-gray-400 focus:border-pink-custom"}`}
                          name="email"
                          id="email"
                          disabled={!!userInfo}
                        />
                        <span className="text-sm text-gray-500">La confirmación del pedido se enviará a esta dirección de correo.</span>
                      </div>

                      <div className="flex flex-col gap-1 mb-2 w-full">
                        <label htmlFor="phone">
                          Teléfono móvil <span className="text-red-500">*</span>
                        </label>
                        <input
                          onChange={handleShippingInput}
                          value={shippingInfo.phone}
                          type="tel"
                          className={`w-full px-3 py-2 outline-none rounded-md 
                            ${errors.phone ? "border-2 border-red-500" : "bg-white border border-gray-400 focus:border-pink-custom"}`}
                          name="phone"
                          id="phone"
                        />
                        <span className="text-sm text-gray-500">El mensajero requiere el número de teléfono.</span>
                      </div>
                    </div>

                    <div className="flex md:flex-col md:gap-2 w-full gap-5 text-slate-600">
                      <div className="flex flex-col gap-1 mb-2 w-full">
                        <label htmlFor="tipoDocumento">DNI/CEDULA EXTRANJERIA</label>
                        <select
                          onChange={handleRegisterInput}
                          value={registerInfo.tipoDocumento}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-400 focus:outline-none focus:ring-pink-custom focus:border-pink-custom sm:text-sm rounded-md border"
                          required
                          name="tipoDocumento"
                          id="tipoDocumento"
                          disabled={!!userInfo}>
                          <option value="">Seleccione</option>
                          <option value="DNI">DNI</option>
                          <option value="CE">Cédula Extranjería</option>
                          <option value="PASAPORTE">Pasaporte</option>
                          <option value="RUC">RUC</option>
                        </select>
                      </div>

                      <div className="flex flex-col gap-1 mb-2 w-full">
                        <label htmlFor="numeroDocumento">
                          Documento <span className="text-red-500">*</span>
                        </label>
                        <input
                          onChange={handleRegisterInput}
                          value={registerInfo.numeroDocumento}
                          type="text"
                          className={`w-full px-3 py-2 outline-none rounded-md 
                            ${errors.numeroDocumento ? "border-2 border-red-500" : "bg-white border border-gray-400 focus:border-pink-custom"}`}
                          name="numeroDocumento"
                          id="numeroDocumento"
                          maxLength={registerInfo.tipoDocumento === "RUC" ? 11 : 8}
                          disabled={!!userInfo}
                        />
                        {loading.consulting && (
                          <div className="absolute right-3 top-9">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-pink-custom"></div>
                          </div>
                        )}
                      </div>
                    </div>

                    <h2 className="text-slate-600 font-bold pb-3">Dirección de envío </h2>
                    <p>Completa estos datos para la facturación </p>

                    <div className="flex md:flex-col md:gap-2 w-full gap-5 text-slate-600">
                      <div className="flex flex-col gap-1 mb-2 w-full">
                        <label htmlFor="name">
                          Nombre <span className="text-red-500">*</span>
                        </label>
                        <input
                          onChange={handleShippingInput}
                          value={shippingInfo.name}
                          type="text"
                          className={`w-full px-3 py-2 outline-none rounded-md 
                            ${errors.name ? "border-2 border-red-500" : "bg-white border border-gray-400 focus:border-pink-custom"}`}
                          name="name"
                          id="name"
                        />
                      </div>

                      <div className="flex flex-col gap-1 mb-2 w-full">
                        <label htmlFor="lastname">
                          Apellido <span className="text-red-500">*</span>
                        </label>
                        <input
                          onChange={handleRegisterInput}
                          value={registerInfo.lastname}
                          type="text"
                          className={`w-full px-3 py-2 outline-none rounded-md 
                            ${errors.lastname ? "border-2 border-red-500" : "bg-white border border-gray-400 focus:border-pink-custom"}`}
                          name="lastname"
                          id="lastname"
                        />
                      </div>
                    </div>

                    <div className="flex md:flex-col md:gap-2 w-full gap-5 text-slate-600">
                      <div className="flex flex-col gap-1 mb-2 w-full">
                        <label htmlFor="address">
                          Dirección <span className="text-red-500">*</span>
                        </label>
                        <input
                          onChange={handleShippingInput}
                          value={shippingInfo.address}
                          type="text"
                          className={`w-full px-3 py-2 outline-none rounded-md 
                            ${errors.address ? "border-2 border-red-500" : "bg-white border border-gray-400 focus:border-pink-custom"}`}
                          name="address"
                          id="address"
                        />
                      </div>
                    </div>

                    <div className="flex md:flex-col md:gap-2 w-full gap-5 text-slate-600">
                      <div className="flex flex-col gap-1 mb-2 w-full">
                        <label htmlFor="province">
                          Provincia <span className="text-red-500">*</span>
                        </label>
                        <select
                          onChange={handleShippingInput}
                          value={shippingInfo.province}
                          className={`w-full px-3 py-2 outline-none rounded-md 
                            ${errors.province ? "border-2 border-red-500" : "bg-white border border-gray-400 focus:border-pink-custom"}`}
                          name="province"
                          id="province"
                          disabled={loading.loadingUbigeo}>
                          <option value="">Seleccione una provincia</option>
                          {provinces.map(prov => (
                            <option key={prov.name} value={prov.name}>
                              {prov.name}
                            </option>
                          ))}
                        </select>
                        {loading.loadingUbigeo && (
                          <div className="absolute right-3 top-9">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-pink-custom"></div>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-1 mb-2 w-full">
                        <label htmlFor="district">
                          Ciudad <span className="text-red-500">*</span>
                        </label>
                        <select
                          onChange={handleShippingInput}
                          value={shippingInfo.city}
                          className={`w-full px-3 py-2 outline-none rounded-md 
                            ${errors.city ? "border-2 border-red-500" : "bg-white border border-gray-400 focus:border-pink-custom"}`}
                          name="city"
                          id="city"
                          disabled={!shippingInfo.province || loading.loadingUbigeo}>
                          <option value="">Seleccione una ciudad</option>
                          {filteredDistricts.map(dist => (
                            <option key={dist.name} value={dist.name}>
                              {dist.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="flex md:flex-col md:gap-2 w-full gap-5 text-slate-600">
                      <div className="flex flex-col gap-1 mb-2 w-full">
                        <label htmlFor="tipoComprobante">TIPO DE COMPROBANTE</label>
                        <select
                          onChange={handleShippingInput}
                          value={shippingInfo.tipoComprobante}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-400 focus:outline-none focus:ring-pink-custom focus:border-pink-custom sm:text-sm rounded-md border"
                          required
                          name="tipoComprobante"
                          id="tipoComprobante">
                          <option value="">Seleccione</option>
                          <option value="BOLETA">BOLETA</option>
                          <option value="FACTURA">FACTURA</option>
                        </select>
                      </div>
                      {shippingInfo.tipoComprobante === "FACTURA" && (
                        <div className="flex flex-col gap-1 mb-2 w-full">
                          <label htmlFor="numeroComprobante">
                            Nro. RUC <span className="text-red-500">*</span>
                          </label>
                          <input
                            onChange={handleShippingInput}
                            value={shippingInfo.numeroComprobante}
                            type="text"
                            className={`w-full px-3 py-2 outline-none rounded-md 
                            ${errors.numeroComprobante ? "border-2 border-red-500" : "bg-white border border-gray-400 focus:border-pink-custom"}`}
                            name="numeroComprobante"
                            id="numeroComprobante"
                            maxLength={11}
                          />
                          {loading.consulting && (
                            <div className="absolute right-3 top-9">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-pink-custom"></div>
                            </div>
                          )}

                          {razonSocial && (
                            <span className="text-sm text-green-600 mt-1">
                              Razón Social: <strong>{razonSocial}</strong>
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {!userInfo && (
                      <div className="flex md:flex-col md:gap-2 w-full gap-5 text-slate-600">
                        <div className="flex flex-col gap-1 mb-2">
                          <label htmlFor="accept_policies" className="flex items-center gap-2 cursor-pointer">
                            <input
                              className="form-check-input checkbox"
                              type="checkbox"
                              name="accept_policies"
                              id="accept_policies"
                              checked={registerInfo.accept_policies}
                              onChange={handleCheckboxChange}
                              required
                            />
                            <span className="text-label">
                              <span className="text-red-500">*</span> He leído y acepto los{" "}
                              <Link to="/terminos-y-condiciones" title="Términos y condiciones" className="text-pink-custom hover:underline">
                                Términos y condiciones
                              </Link>{" "}
                              y{" "}
                              <Link to="/politica-de-seguridad-y-privacidad" title="Seguridad y privacidad" className="text-pink-custom hover:underline">
                                Política de Privacidad
                              </Link>
                            </span>
                          </label>
                        </div>
                        <div className="flex flex-col gap-1 mb-2">
                          <label htmlFor="personaldata" className="flex items-center gap-2 cursor-pointer">
                            <input className="form-check-input checkbox" type="checkbox" name="personaldata" id="personaldata" checked={registerInfo.personaldata} onChange={handleCheckboxChange} />
                            <span className="text-label">Autorizo el tratamiento de mis datos para fines de prospección y promoción comercial</span>
                          </label>
                        </div>
                      </div>
                    )}

                    <div className="flex md:flex-col md:gap-2 w-full gap-5 text-slate-600">
                      <div className="flex flex-col gap-1 mt-7 mb-2 w-full">
                        <button
                          type="submit"
                          className="px-3 py-[6px] rounded-sm hover:shadow-pink-custom/50 hover:shadow-lg bg-custom-rosado text-white disabled:opacity-50"
                          disabled={loading.registering || loading.migrating || loading.placingOrder || loading.loadingUbigeo}>
                          {loading.registering && "Registrando..."}
                          {loading.migrating && "Migrando carrito..."}
                          {loading.placingOrder && "Creando pedido..."}
                          {loading.loadingUbigeo && "Cargando datos de ubicación..."}
                          {!loading.registering && !loading.migrating && !loading.placingOrder && !loading.loadingUbigeo && "CONTINUAR CON EL PAGO"}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>

                {products.map((p, i) => (
                  <div key={i} className="flex bg-white p-4 flex-col gap-2">
                    <div className="flex justify-start items-center">
                      <h2 className="text-md text-slate-600 font-bold">{p.shopName}</h2>
                    </div>

                    {p.products.map((pt, i) => (
                      <div className="w-full flex flex-wrap" key={i}>
                        <div className="flex sm:w-full gap-2 w-7/12">
                          <div className="flex gap-2 justify-start items-center">
                            <img className="w-[80px] h-[80px]" src={pt.productInfo.image} alt="" />
                            <div className="pr-4 text-slate-600">
                              <p className="text-85 font-semibold">{pt.productInfo.name} </p>
                              <span className="text-sm">Marca: {pt.productInfo.brand}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-between w-5/12 sm:w-full sm:mt-3">
                          <div className="pl-4 sm:pl-0">
                            <h2 className="text-lg text-orange-500">S/{pt.productInfo.discount > 0 ? pt.productInfo.discount : pt.productInfo.price}</h2>
                            {pt.productInfo.discount > 0 && <p className="line-through">S/{pt.productInfo.price}</p>}
                            {pt.productInfo.discount > 0 && <p>{Math.round(((pt.productInfo.price - pt.productInfo.discount) / pt.productInfo.price) * 100)}%</p>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div className="w-[33%] md-lg:w-full">
              <div className="pl-3 md-lg:pl-0 md-lg:mt-5">
                <div className="bg-white p-3 text-slate-600 flex flex-col gap-3">
                  <h2 className="text-xl font-bold">Resumen de Pedido</h2>
                  <div className="flex justify-between items-center">
                    <span>
                      Subtotal ( {items} {items === 1 ? "producto" : "productos"}){" "}
                    </span>
                    <span>S/{price}</span>
                  </div>

                  {/* Sección modificada para opciones de envío */}
                  <div className="flex flex-col gap-4 mt-4">
                    <h3 className="font-bold text-slate-700">Método de envío</h3>

                    <div className="flex flex-col gap-3">

                      {/* Banner FELICIDADES - envío gratis superado */}
                      {isFreeShipping && (
                        <div className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl px-5 py-4 flex items-center gap-3 shadow-lg animate-pulse-once">
                          <span className="text-3xl">🎉</span>
                          <div>
                            <p className="font-bold text-lg leading-tight">{mensajeEnvioGratis}</p>
                            <p className="text-green-100 text-xs mt-0.5">Tu compra supera S/{sendFreeAmount} — ¡sin costo de envío!</p>
                          </div>
                        </div>
                      )}

                      {/* Envío gratis - radio button */}
                      {isFreeShipping && (
                        <label
                          className={`relative flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                            shippingMethod === "gratis"
                              ? "border-green-500 bg-green-50 ring-1 ring-green-500"
                              : "border-gray-200 hover:border-gray-300 bg-white"
                          }`}
                        >
                          <input
                            type="radio"
                            id="gratis"
                            name="shipping"
                            value="0"
                            checked={shippingMethod === "gratis"}
                            onChange={() => {
                              setShippingFee(0);
                              setShippingMethod("gratis");
                            }}
                            className="mt-1 w-4 h-4 text-green-500 focus:ring-green-500 border-gray-300"
                          />
                          <div className="flex flex-col">
                            <span className="font-semibold text-green-700 flex items-center gap-2">
                              🚚 Envío Gratis
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">GRATIS</span>
                            </span>
                            <span className="text-xs text-slate-500 mt-1">
                              Tu compra supera S/{sendFreeAmount} — ¡envío sin costo!
                            </span>
                          </div>
                        </label>
                      )}

                      {/* Banner informativo si no llega al monto */}
                      {sendFreeAmount > 0 && !isFreeShipping && (
                        <div className="text-xs text-slate-600 bg-yellow-50 border border-yellow-300 rounded-lg px-3 py-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span>📦</span>
                            <span>Agrega <strong>S/{(sendFreeAmount - price).toFixed(2)}</strong> para que tu envío sea gratis</span>
                          </div>
                          <Link
                            to="/shops"
                            className="bg-[#ae1d22] hover:bg-[#8b1519] text-white font-semibold py-1.5 px-4 rounded-md transition-colors text-center whitespace-nowrap shadow-sm text-xs"
                          >
                            Seguir agregando
                          </Link>
                        </div>
                      )}

                      {/* Opción: A coordinar - solo visible si NO aplica envío gratis */}
                      {!isFreeShipping && (
                        <label
                          className={`relative flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                            shippingMethod === "sincosto"
                              ? "border-[#ae1d22] bg-red-50 ring-1 ring-[#ae1d22]"
                              : "border-gray-200 hover:border-gray-300 bg-white"
                          }`}
                        >
                          <input
                            type="radio"
                            id="sincosto"
                            name="shipping"
                            value="0"
                            checked={shippingMethod === "sincosto"}
                            onChange={() => {
                              setShippingFee(0);
                              setShippingMethod("sincosto");
                            }}
                            className="mt-1 w-4 h-4 text-[#ae1d22] focus:ring-[#ae1d22] border-gray-300"
                          />
                          <div className="flex flex-col">
                            <span className="font-semibold text-slate-700">Costo de envío a coordinar</span>
                            <span className="text-xs text-slate-500 mt-1 leading-relaxed">
                              El costo final del envío se realizará previa comunicación con un asesor de ventas.
                            </span>
                          </div>
                        </label>
                      )}

                    </div>
                  </div>
                  {/* Fin de sección modificada */}

                  <div className="flex justify-between items-center">
                    <span>Total a Pagar</span>
                    <span className="text-lg text-[#FF3A59]">S/{price + shipping_fee} </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Shipping;
